import { FC, JSX, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";

import { ModalLink } from "@app/shared/Modals";
import FlyoutLink from "@app/shared/Flyouts/FlyoutLink";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import TutorialsService from "@app/tutorials/tutorialsService";
import { TutorialContent, loadingData, errorData } from "@app/tutorials/tutorialsShared";
import { EditTutorialsFlyout } from "@app/admin/tutorials/EditTutorialsFlyout";
import { EditTutorialsHyperlinkFlyout } from "@app/admin/tutorials/EditTutorialsHyperlinkFlyout";
import { AddTutorialFlyout } from "@app/admin/tutorials/AddTutorialFlyout";
import { AddTutorialHyperlinkFlyout } from "@app/admin/tutorials/AddTutorialHyperlinkFlyout";

interface TutorialListProps {
  containerName: string;
  friendlyName?: string;
  editOnly?: boolean;
  enableHyperlink?: boolean;
}

export const EditTutorialsList: FC<TutorialListProps> = (props: TutorialListProps) => {
  const [draggedFieldId, setDraggedFieldId] = useState<number>();
  const [dropFieldId, setDropFieldId] = useState<number>();
  const [tutorials, setTutorials] = useState(loadingData);
  const supportService = useRef(new TutorialsService()).current;

  useEffect(() => {
    loadContent();
  }, []);

  const loadContent = () => {
    supportService.showBlobSas(props.containerName)
      .then((tutorialsStr) => {
        setTutorials(JSON.parse(tutorialsStr));
      })
      .catch(() => {
        setTutorials(errorData);
      });
  }

  const dragDropPadding = (listLen: number) => {
    const expanded = isDragging();
    const insertable = dropFieldId == listLen;
    return (
      <div
        style={
          {
            borderTop: insertable && expanded ? '2px dotted gray' : 'none',
            height: expanded ? '40px' : '0px',
            transition: '0.1s height ease-out'
          }
        }
        onDragEnter={() => { dragEnterHandler(listLen); }}
      />
    );
  }

  const newFileButton = () => {
    return (
      !(props.editOnly ?? false) && (
        <FlyoutLink
          content={{
            body: <AddTutorialFlyout
              container={props.containerName}
              fileExtensionSplitter={fileExtensionSplitter}
              onUpload={(displayTitle: string, newDataStr: string) => {
                toast(<span>Tutorial &quot;{displayTitle}&quot; successfully added</span>, {
                  containerId: "z-index-9999",
                  type: "success",
                });
                updateContainerUI(newDataStr);
              }}
            />,
            title: "Add Tutorial File",
          }}
          classNames="qes-button-secondary"
          testId="edit-tutorials__add-new-flyout"
          size="40"
        >
          <QesIcon iconName={QesIcons.Plus} description="Add" className="mb3" /> Add New File to {props.friendlyName ?? props.containerName}
        </FlyoutLink>
      )
    );
  }

  const newHyperlinkButton = () => {
    return (
      !(props.editOnly ?? false) && (props.enableHyperlink ?? false) && (
        <div>
          <FlyoutLink
            content={{
              body: <AddTutorialHyperlinkFlyout
                container={props.containerName}
                fileExtensionSplitter={fileExtensionSplitter}
                onSubmit={(displayTitle: string, newDataStr: string) => {
                  toast(<span>Tutorial &quot;{displayTitle}&quot; successfully added</span>, {
                    containerId: "z-index-9999",
                    type: "success",
                  });
                  updateContainerUI(newDataStr);
                }}
              />,
              title: "Add Tutorial Hyperlink",
            }}
            classNames="qes-button-secondary"
            testId="edit-tutorials__add-new-flyout"
            size="40"
          >
            <QesIcon iconName={QesIcons.Plus} description="Add" className="mb3" /> Add Hyperlink to {props.friendlyName ?? props.containerName}
          </FlyoutLink>
        </div>
      )
    );
  }

  const isDragging = () => draggedFieldId != null;

  const dragStartHandler = (index: number) => {
    setDraggedFieldId(index);
    setDropFieldId(index);
  }

  const dragEndHandler = (index: number) => {
    if (isDragging()) {
      const newIndex = dropFieldId;
      const reordered = tutorials;
      const editHyperlink = "null";

      const draggedField = reordered[index];

      if (newIndex != index) {
        const formData = new FormData();
        formData.set("index", newIndex.toString());

        if (draggedField.filetype == "txt") {
          supportService.updateHyperlinkBlobSas(props.containerName, draggedField.title, editHyperlink, newIndex)
            .then((response) => { updateContainerUI(response) });
        }
        else {
          supportService.updateBlobSas(props.containerName, draggedField.filename, draggedField.filetype, draggedField.title, formData)
            .then((response) => { updateContainerUI(response) });
        }
      }

      // indexes shift as we do this, so this ensures correctness
      if (index > newIndex) {
        reordered.splice(index, 1);
        reordered.splice(newIndex, 0, draggedField);
      } else {
        reordered.splice(newIndex, 0, draggedField);
        reordered.splice(index, 1);
      }

      setDraggedFieldId(null);
      setDropFieldId(null);
      setTutorials(reordered);
    }
  }

  const dragEnterHandler = (index: number) => {
    if (isDragging()) {
      setDropFieldId(index);
    }
  }

  const renderLines = (row: TutorialContent, index: number) => {
    let linkImageElement: JSX.Element | null;
    let flyoutLinkBody: JSX.Element;
    switch (row.filetype) {
      case "pdf":
        linkImageElement = null;
        flyoutLinkBody = (<EditTutorialsFlyout
          containerReference={row.path}
          containerItem={row.filename}
          itemExtension={row.filetype}
          fileExtensionSplitter={fileExtensionSplitter}
          rowTitle={row.title}
          updateContainerUI={updateContainerUI}
        />);
        break;
      case "mp4":
        linkImageElement = (
          <QesIcon iconName={QesIcons.Play} description="Play" className="text-info" />
        );
        flyoutLinkBody = (<EditTutorialsFlyout
          containerReference={row.path}
          containerItem={row.filename}
          itemExtension={row.filetype}
          fileExtensionSplitter={fileExtensionSplitter}
          rowTitle={row.title}
          updateContainerUI={updateContainerUI}
        />);
        break;
      case "txt":
        linkImageElement = (
          <QesIcon iconName={QesIcons.LinkV4} description="Open" className="text-info" />
        );
        flyoutLinkBody = (<EditTutorialsHyperlinkFlyout
          containerReference={row.path}
          containerItem={row.filename}
          itemExtension={row.filetype}
          rowTitle={row.title}
          updateContainerUI={updateContainerUI}
          hyperlinkURL={row.link}
        />);
        break;
      default:
        linkImageElement = null;
        flyoutLinkBody = (<EditTutorialsFlyout
          containerReference={row.path}
          containerItem={row.filename}
          itemExtension={row.filetype}
          fileExtensionSplitter={fileExtensionSplitter}
          rowTitle={row.title}
          updateContainerUI={updateContainerUI}
        />);
    }

    const insertIndicator = index === dropFieldId
      ? { borderTop: '2px dotted gray' }
      : {};

    return (
      <div
        className={"d-flex flex-row align-items-center w-100 pb10 pt10 grid-row"}
        style={insertIndicator}
        key={index}
        onDragEnter={() => dragEnterHandler(index)}
        onDragEnd={() => dragEndHandler(index)}
      >

        <p className="ml10 mb0 mr10">
          <span
            className="drag-indicator"
            onDragStart={() => dragStartHandler(index)}
            draggable
          />
          <a className="text-decoration-none" style={{ color: "inherit" }} href={row.link} target="_blank" rel="noreferrer">
            {linkImageElement} <span className="mb0 mt0 ml10">{row.title}</span>
          </a>
        </p>
        <span className="btn-group ms-auto">
          <span className="ml5 mr10">
            <FlyoutLink
              content={{
                body: flyoutLinkBody,
                title: `Edit ${row.title}`,
              }}
              classNames="qes-button-secondary"
              testId="edit-tutorials__edit-flyout"
              size="40"
            >
              <QesIcon iconName={QesIcons.PencilV1} description="Edit" className="mb3" /> Edit
            </FlyoutLink>
          </span>
          <span className="d-inline-block ms-3">
            <ModalLink
              classNames="qes-button-secondary danger"
              content={{
                body: "Are you sure you want to delete this tutorial?",
                cancelButtonText: "Cancel",
                confirmButtonAction: () => deleteRow(row.path, row.filename, row.filetype, row.title),
                confirmButtonText: "Delete",
                confirmButtonType: "primary-danger",
                title: `Delete ${row.title}`,
              }}
              testId="edit-tutorials-list__delete-modal"
              hideBodyScroll={true}
            >
              Delete
            </ModalLink>
          </span>
        </span>
      </div>
    );
  }

  const deleteRow = async (containerReference: string, containerItem: string, itemExtension: string, displayTitle: string) => {
    const newDataStr = await supportService.deleteBlobSas(containerReference, containerItem, itemExtension);
    updateContainerUI(newDataStr);
    toast(<span>Tutorial &quot;{displayTitle}&quot; successfully deleted</span>, {
      containerId: "z-index-9999",
      type: "success",
    });
  }

  const updateContainerUI = (newDataStr: string) => {
    const newData: TutorialContent[] = JSON.parse(newDataStr);
    setTutorials(newData);
  }

  const buildRows = tutorials.map((row, index) => renderLines(row, index));

  return (
    <div className="grid-striped">
      {buildRows}
      {dragDropPadding(tutorials.length)}
      {newFileButton()}
      {newHyperlinkButton()}
    </div>
  );
}

function fileExtensionSplitter(filename: string): [string, string] {
  const splitname = filename.split('.');
  const extension = (splitname.length > 1) ? splitname.pop() : "";
  const basename = splitname.join("");
  return [basename, extension];
}

